import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Group, Select, Stack } from "@mantine/core";

import { notifyUserFacingError } from "@util/toErrorMessage";

export default function UserOrganizationAssociate({
  userId,
  organizations = [],
  onSuccess = () => {},
  onCancel = null,
}) {
  const { t } = useTranslation();
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const memberOrgIds = useMemo(
    () =>
      (organizations || [])
        .map((organization) => String(organization.id))
        .sort(),
    [organizations]
  );

  useEffect(() => {
    const memberSet = new Set(memberOrgIds);
    setLoadingOrgs(true);
    axios
      .post(`/retrieve-organizations/`, {
        scope: "active",
        page: 0,
        page_size: 200,
      })
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        setOrganizationOptions(
          rows
            .filter((organization) => !memberSet.has(String(organization.id)))
            .map((organization) => ({
              value: String(organization.id),
              label: organization.name || String(organization.id),
            }))
        );
        setLoadingOrgs(false);
      })
      .catch((err) => {
        setLoadingOrgs(false);
        notifyUserFacingError(err, toast, {
          source: "user_organization_associate_list",
        });
      });
  }, [memberOrgIds.join(",")]);

  function onAdd() {
    if (!organizationId || !userId) return;

    setSubmitting(true);
    axios
      .post(`/organizations/${organizationId}/users/`, { user_id: userId })
      .then(() => {
        setSubmitting(false);
        setOrganizationId(null);
        toast.success(t("updated"));
        onSuccess();
      })
      .catch((err) => {
        setSubmitting(false);
        notifyUserFacingError(err, toast, {
          source: "user_organization_associate",
        });
      });
  }

  return (
    <Stack gap="sm">
      <Select
        searchable
        clearable
        label={t("cs:organization_singular")}
        placeholder={t("cs:organization_singular")}
        data={organizationOptions}
        value={organizationId}
        onChange={setOrganizationId}
        disabled={loadingOrgs}
      />
      <Group>
        <Button
          disabled={!organizationId}
          loading={submitting}
          onClick={onAdd}
        >
          {t("add")}
        </Button>
        {onCancel && (
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            {t("cancel")}
          </Button>
        )}
      </Group>
    </Stack>
  );
}
